import { Component } from 'react';
import classNames from 'classnames';
import Autosuggest from 'react-autosuggest';
import styled, { css } from 'styled-components';

import api from 'src/utils/api';
import i18n from 'src/utils/translate';
import { throttle } from 'lodash';
import { SearchBarIconButton } from 'styles-js/buttons';

type State = {
  query: string;
  focus: boolean;
  suggestions: string[];
}
type Props = {
  colorError: string;
  placeholderText: string;
  onSearch: (text: string) => void;
  error?: string;
  setSearchQueryEmpty: (bool: boolean) => void;
}
export default class SearchBar extends Component<Props, State> {

  static defaultProps = {
    colorError: '$color-error',
    placeholderText: '',
    onSearch: () => {},
  }

  input: any;

  constructor(props) {
    super(props);
    this.state = {
      focus: false,
      query: '',
      suggestions: [],
    };
  }

  onFocus = () => {
    this.setState({ focus: true });
  }

  onBlur = () => {
    this.setState({ focus: false });
  }

  handleSubmit = (e) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (e) e.preventDefault();

    this.props.onSearch(this.state.query);
    this.setState({ focus: false });
  }

  onSuggestionsFetchRequested = throttle(async ({ value = '' }) => {
    if (value.length <= 1) {
      return;
    }
    const { data } = await api.getPublic(`search/autocomplete?q=${value}`);
    this.setState({
      // TODO: remove toLowerCase when the b/e is fixed
      suggestions: data.slice(0, 10).map(x => x.toLowerCase()),
    });
  }, 500);

  onSuggestionsClearRequested = () => {
    this.setState({ suggestions: [] });
  }

  onSuggestionSelected = (e, { suggestion }) => {
    this.setState({ query: suggestion }, () => {
      this.handleSubmit(e);
    });
  }

  renderInputComponent = (props) => {
    const inputContainerClassName = classNames({
      'search-bar__container': true,
      'search-bar__container--focused': this.state.focus && !this.props.error,
      'search-bar__container--error': this.props.error,
    });
    const customProps = {
      ...props,
      'aria-label': i18n.t('Start a search'),
      ref: (node) => {
        this.input = node;
        props.ref(node);
      },
    };
    return (
      <div>
        <label className={inputContainerClassName}>
          <input {...customProps}/>
          <SearchBarIconButton
            className="solo-icon"
            disabled={!this.state.query}
            type="submit"
          >
            <span className="icon icon-search-1" />
            <span className="search-bar__submit-text">{ i18n.t('Search') }</span>
          </SearchBarIconButton>
        </label>
        {this.props.error && <ValidationError>{this.props.error}</ValidationError>}
      </div>
    );
  }

  renderSuggestion = (text) => {
    const highlight = this.state.query;
    // queries with special characters (e.g. 'Pregnancy (difficulties)') were breaking the RegExp so they are now escaped
    const escapedHighlight = highlight.replace(/[-[\]{}()*+?.,\\^$|#]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escapedHighlight})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) =>
          <span
            key={i}
            style={part.toLowerCase() === highlight.toLowerCase() ? { fontWeight: 'bold' } : {}}
          >
            {part}
          </span>)}
      </span>
    );
  }

  render() {
    const inputProps = {
      className: 'search-bar__input',
      placeholder: this.props.placeholderText,
      onChange: (e, { newValue }) => {
        this.setState({ query: newValue });
        this.props.setSearchQueryEmpty(newValue.trim().length < 1 || newValue === null);
      },
      value: this.state.query,
      type: 'text',
      onBlur: this.onBlur,
      onFocus: this.onFocus,
    };
    return (
      <SearchBarWrapper
        className="search-bar"
        isError={Boolean(this.props.error)}
      >
        <form
          className="search-bar__form"
          onSubmit={this.handleSubmit}
        >
          <Autosuggest
            getSuggestionValue={suggestion => suggestion}
            id="community-search"
            inputProps={inputProps}
            renderInputComponent={this.renderInputComponent}
            renderSuggestion={this.renderSuggestion}
            suggestions={this.state.suggestions}
            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
            onSuggestionSelected={this.onSuggestionSelected}
            onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          />
        </form>
      </SearchBarWrapper>
    );
  }
}

const SearchBarWrapper = styled.div<{isError: boolean}>`
  ${({ isError }) => isError && css`
    height: 110px;
  `}
`;

const ValidationError = styled.div`
   color: ${({ theme }) => theme.colorError};
   padding: 10px 0 20px;
`;
